import { useState } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { toast } from 'react-toastify';
import DateRangeSelector from '../../../components/DateRangeSelector';
import {
  getSalesReport,
  SalesReport,
} from '../services/OrderCRUD';
import { OrderOriginEnum } from '../models/OrderModel';
import { ORIGIN_LABEL_IDS } from './OrderList';

export default function B2BSalesReport() {
  const intl = useIntl();
  const initialSince = new Date(
    new Date().setDate(new Date().getDate() - new Date().getDay() + 1)
  );
  const initialUntil = new Date(
    new Date().setDate(new Date().getDate() - new Date().getDay() + 7)
  );
  const [since, setSince] = useState(initialSince);
  const [until, setUntil] = useState(initialUntil);
  const [origin, setOrigin] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<SalesReport | null>(null);

  async function fetchReport(
    sinceDate: Date,
    untilDate: Date,
    originValue: string | null
  ) {
    try {
      setLoading(true);
      const { data } = await getSalesReport({
        since: moment(sinceDate).format('YYYY-MM-DD'),
        until: moment(untilDate).format('YYYY-MM-DD'),
        origin: originValue || undefined,
      });
      setReport(data);
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'b2bSalesReport.error' }));
      console.warn(error);
    } finally {
      setLoading(false);
    }
  }

  function handleDateChange(newSince: Date, newUntil: Date) {
    setSince(newSince);
    setUntil(newUntil);
    fetchReport(newSince, newUntil, origin);
  }

  function handleOriginChange(newOrigin: string | null) {
    setOrigin(newOrigin);
    fetchReport(since, until, newOrigin);
  }

  return (
    <>
      <h1 className="title mb-8">
        {intl.formatMessage({ id: 'b2bSalesReport.title' })}
      </h1>
      <div className="d-flex gap-4 align-items-end">
        <DateRangeSelector
          initialSince={initialSince}
          initialUntil={initialUntil}
          onSelected={handleDateChange}
          disableFutureDates={false}
        />
        <div className="form-group w-180px d-inline-block mb-4">
          <label className="form-label mb-2">
            {intl.formatMessage({ id: 'b2bOrders.filterByOrigin' })}
          </label>
          <select
            className="form-control form-select"
            value={origin || ''}
            onChange={(e) => handleOriginChange(e.target.value || null)}
          >
            <option value="">
              {intl.formatMessage({ id: 'b2bOrders.anyOrigin' })}
            </option>
            {Object.values(OrderOriginEnum).map((originValue) => (
              <option key={originValue} value={originValue}>
                {intl.formatMessage({ id: ORIGIN_LABEL_IDS[originValue] })}
              </option>
            ))}
            <option value="none">
              {intl.formatMessage({ id: 'b2bOrders.originUnspecified' })}
            </option>
          </select>
        </div>
        {loading && (
          <span className="mb-4 spinner-border text-primary spinner-border-sm me-3" />
        )}
      </div>

      {report && (
        <div className="d-flex flex-wrap gap-10 mt-8">
          <section className="card shadow p-10" style={{ width: 'fit-content' }}>
            <h2 className="mb-6">
              {intl.formatMessage({ id: 'b2bSalesReport.byVariant' })}
            </h2>
            {report.by_variant.length > 0 ? (
              <table className="table table-striped table-hover table-bordered">
                <thead className="table-light fw-bold">
                  <tr>
                    <th className="ps-4">
                      {intl.formatMessage({ id: 'b2bSalesReport.variant' })}
                    </th>
                    <th className="text-end pe-4">
                      {intl.formatMessage({ id: 'b2bSalesReport.units' })}
                    </th>
                    <th className="text-end pe-4">
                      {intl.formatMessage({ id: 'b2bSalesReport.totalKg' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.by_variant.map((row) => (
                    <tr key={row.variant_id}>
                      <td className="ps-4">{row.variant_name}</td>
                      <td className="text-end pe-4">{row.total_units}</td>
                      <td className="text-end pe-4">
                        {intl.formatNumber(row.total_kg, {
                          minimumFractionDigits: 3,
                          maximumFractionDigits: 3,
                        })}{' '}
                        kg
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>{intl.formatMessage({ id: 'b2bSalesReport.noData' })}</p>
            )}
          </section>

          <section className="card shadow p-10" style={{ width: 'fit-content' }}>
            <h2 className="mb-6">
              {intl.formatMessage({ id: 'b2bSalesReport.byRecipe' })}
            </h2>
            {report.by_recipe.length > 0 ? (
              <table className="table table-striped table-hover table-bordered">
                <thead className="table-light fw-bold">
                  <tr>
                    <th className="ps-4">
                      {intl.formatMessage({ id: 'b2bSalesReport.recipe' })}
                    </th>
                    <th className="text-end pe-4">
                      {intl.formatMessage({ id: 'b2bSalesReport.totalKg' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.by_recipe.map((row) => (
                    <tr key={row.recipe_id}>
                      <td className="ps-4">{row.recipe_name}</td>
                      <td className="text-end pe-4">
                        {intl.formatNumber(row.total_kg, {
                          minimumFractionDigits: 3,
                          maximumFractionDigits: 3,
                        })}{' '}
                        kg
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>{intl.formatMessage({ id: 'b2bSalesReport.noData' })}</p>
            )}
          </section>
        </div>
      )}
    </>
  );
}
